import { Component } from "@theme/component";
import { VariantSelectedEvent, VariantUpdateEvent } from "@theme/events";
import { morph } from "@theme/morph";
import { requestYieldCallback } from "@theme/utilities";

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {boolean} */
  #shouldAutoSelectSize = false;

  /** @type {boolean} */
  #isUpdatingVisibility = false;

  /** @type {boolean} */
  #isInitialized = false;

  /** @type {((event: Event) => void) | undefined} */
  #boundChangeHandler;

  /** @type {Array} */
  #variants = [];

  /** @type {Array} */
  #options = [];

  connectedCallback() {
    super.connectedCallback();

    // Prevent adding duplicate event listeners if already initialized
    if (this.#isInitialized) return;
    this.#isInitialized = true;

    // Store bound handler reference for cleanup
    this.#boundChangeHandler = this.variantChanged.bind(this);
    this.addEventListener("change", this.#boundChangeHandler);

    // Parse variant data for cart-upsell option visibility
    if (this.dataset.cartUpsell === "true") {
      this.#parseVariantData();
      this.updateOptionVisibility();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();

    // Clean up event listener
    if (this.#boundChangeHandler) {
      this.removeEventListener("change", this.#boundChangeHandler);
    }
    this.#isInitialized = false;
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    // Check if this is a color option change (for cart-upsell context)
    const isColorOption = this.isColorOption(event.target);
    const isCartUpsell = this.dataset.cartUpsell === "true";
    const isColorsOnly = this.dataset.colorsOnly === "true";

    if (isCartUpsell && isColorsOnly) {
      this.updateSelectedOption(event.target);
      this.#updateSelectedStates();

      const optionValueId = selectedOption.dataset.optionValueId;
      if (!optionValueId) return;

      const mainPicker = this.#getMainCartUpsellPicker();
      if (!mainPicker) return;

      const mainInput = mainPicker.querySelector(`[data-option-value-id="${optionValueId}"]`);
      if (!(mainInput instanceof HTMLInputElement)) return;

      mainPicker.updateSelectedOption(mainInput);
      mainInput.dispatchEvent(new Event("change", { bubbles: true }));
      return;
    }

    this.updateSelectedOption(event.target);
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? "" }));

    const isOnProductPage =
      this.dataset.templateProductMatch === "true" &&
      !event.target.closest("product-card") &&
      !event.target.closest("quick-add-dialog");

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split("?")[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    // Store flag to auto-select first available size after color change
    if (isCartUpsell && isColorOption) {
      this.#shouldAutoSelectSize = true;
    }

    this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), loadsNewProduct);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set("variant", variantId);
      } else {
        url.searchParams.delete("variant");
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, "", url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === "string") {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error("Target element not found");

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find(option => option.value === newValue);

      if (!newSelectedOption) throw new Error("Option not found");

      for (const option of target.options) {
        option.removeAttribute("selected");
      }

      newSelectedOption.setAttribute("selected", "selected");
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(",")}`);
    } else if (source === "product-card") {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(",")}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest("quick-add-component") || this.closest("swatches-variant-picker-component")) {
      if (productUrl?.includes("?")) {
        productUrl = productUrl.split("?")[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join("&")}`;
    }

    // For cart-upsell context, use section-rendering-product-card to get variant picker updates
    // This ensures we get the correct variant options when selecting colors/sizes
    if (this.dataset.cartUpsell === "true") {
      if (productUrl?.includes("?")) {
        productUrl = productUrl.split("?")[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join("&")}`;
    }

    return `${productUrl}?${params.join("&")}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then(response => response.text())
      .then(responseText => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, "text/html");
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector("overflow-list[defer]")?.removeAttribute("defer");

        const textContent = html.querySelector(`variant-picker script[type="application/json"]`)?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // Re-parse variant data and update option visibility after DOM morph (cart-upsell context)
          if (this.dataset.cartUpsell === "true") {
            requestYieldCallback(() => {
              this.#parseVariantData();
              this.updateOptionVisibility();
            });
          }

          // Auto-select first available size after color change (cart-upsell context)
          if (this.#shouldAutoSelectSize) {
            this.#shouldAutoSelectSize = false;
            requestYieldCallback(() => {
              this.autoSelectFirstAvailableSize();
            });
          }

          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? "",
                newProduct,
              })
            );
          }
        }
      })
      .catch(error => {
        if (error.name === "AbortError") {
          console.warn("Fetch aborted by user");
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error("No new variant picker source found");
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;

      if (this.dataset.optionsOnly === "true") {
        this.#hideColorFieldsets(newVariantPickerSource);
      }
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector("main");
    const newMain = newHtml.querySelector("main");

    if (!main || !newMain) {
      throw new Error("No new main source found");
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector("select option[selected], fieldset input:checked");

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error("No option value ID found");
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll("select option[selected], fieldset input:checked"));

    return selectedOptions.map(option => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error("No option value ID found");

      return optionValueId;
    });
  }

  /**
   * Checks if an element is a color option input.
   * @param {HTMLElement} element - The element to check.
   * @returns {boolean} True if the element is a color option.
   */
  isColorOption(element) {
    if (!element) return false;

    // Check if the input is inside a color swatch label
    const swatchLabel = element.closest(".variant-swatch__label");
    if (swatchLabel) return true;

    // Check if the input name contains "color" (case-insensitive)
    if (element instanceof HTMLInputElement || element instanceof HTMLSelectElement) {
      const name = element.name || "";
      const nameLower = name.toLowerCase();
      if (nameLower.includes("color")) return true;
    }

    // Check if the fieldset legend contains "color"
    const fieldset = element.closest("fieldset");
    if (fieldset) {
      const legend = fieldset.querySelector("legend");
      if (legend) {
        const legendText = legend.textContent?.toLowerCase() || "";
        if (legendText.includes("color")) return true;
      }
    }

    return false;
  }

  /**
   * Automatically selects the first available size option after a color change.
   * This is used in cart-upsell context to ensure a valid variant is always selected.
   */
  autoSelectFirstAvailableSize() {
    // Find all non-color option fieldsets (size, waist, height, etc.)
    const allFieldsets = Array.from(this.querySelectorAll("fieldset.variant-option"));
    const nonColorFieldsets = allFieldsets.filter(fieldset => {
      // Skip color swatch fieldsets
      if (fieldset.classList.contains("variant-option--color-swatches")) {
        return false;
      }

      // Check if legend contains "color"
      const legend = fieldset.querySelector("legend");
      if (legend) {
        const legendText = legend.textContent?.toLowerCase() || "";
        if (legendText.includes("color")) {
          return false;
        }
      }

      return true;
    });

    // For each non-color fieldset, find the first available option
    for (const fieldset of nonColorFieldsets) {
      const inputs = Array.from(fieldset.querySelectorAll('input[type="radio"]'));

      // Find the first available input that's not disabled
      const firstAvailable = inputs.find(input => {
        if (!(input instanceof HTMLInputElement)) return false;
        const isAvailable = input.dataset.optionAvailable === "true";
        const isNotDisabled = !input.disabled;
        return isAvailable && isNotDisabled;
      });

      if (firstAvailable && firstAvailable instanceof HTMLInputElement) {
        // Check if it's not already selected
        if (!firstAvailable.checked) {
          // Select the first available option
          firstAvailable.checked = true;
          // Trigger change event but prevent it from triggering another auto-select
          this.#shouldAutoSelectSize = false;
          firstAvailable.dispatchEvent(new Event("change", { bubbles: true }));
          break; // Only select the first available option in the first non-color fieldset
        }
      }
    }
  }

  /**
   * Parses JSON data from script elements for variant visibility logic.
   * Used in cart-upsell context to determine which options should be visible.
   */
  #parseVariantData() {
    const variantsScript = this.querySelector("[data-all-variants-data]");
    const optionsScript = this.querySelector("[data-product-options]");

    if (variantsScript) {
      try {
        this.#variants = JSON.parse(variantsScript.textContent || "[]");
      } catch (e) {
        console.error("[VariantPicker] Error parsing variants data:", e);
      }
    }

    if (optionsScript) {
      try {
        this.#options = JSON.parse(optionsScript.textContent || "[]");
      } catch (e) {
        console.error("[VariantPicker] Error parsing options data:", e);
      }
    }
  }

  /**
   * Gets the position of the color option.
   * @returns {number | null} The position of the color option, or null if not found.
   */
  #getColorOptionPosition() {
    // Use includes to match "Color", "Color (Primary)", etc. - same as Liquid template
    const colorOption = this.#options.find(option => option.name.toLowerCase().includes("color"));
    return colorOption?.position ?? null;
  }

  /**
   * Gets the currently selected options from checked radio inputs.
   * @returns {Object<string, string>} Map of option position to selected value.
   */
  #getSelectedOptions() {
    const selectedOptions = {};
    const inputs = this.querySelectorAll('input[type="radio"]:checked');

    inputs.forEach(input => {
      if (!(input instanceof HTMLInputElement)) return;

      // Extract position from the data-input-id attribute (format: "position-index")
      const inputId = input.dataset.inputId;
      if (inputId) {
        const position = inputId.split("-")[0];
        selectedOptions[position] = input.value;
      }
    });

    return selectedOptions;
  }

  /**
   * Finds a variant that matches the given option selections.
   * @param {Object<string, string>} testOptions - Map of option position to value to test.
   * @returns {Object | null} The matching variant, or null if not found.
   */
  #findVariantForOptions(testOptions) {
    return (
      this.#variants.find(variant => {
        return this.#options.every((option, i) => {
          const optionPosition = option.position.toString();
          // All positions in testOptions must match the variant
          if (optionPosition in testOptions) {
            const optionValue = variant.options[i];
            return testOptions[optionPosition] === optionValue;
          }
          // If position not in testOptions, skip the check (for partial matches)
          return true;
        });
      }) ?? null
    );
  }

  /**
   * Updates the visibility of option values based on the current selection.
   * Hides options that don't have any valid variant combinations.
   * This is used in cart-upsell context for products with 3+ options (e.g., color, waist, length).
   * Products with only 2 options (e.g., color + size) don't need this visibility logic.
   */
  updateOptionVisibility() {
    if (this.dataset.cartUpsell !== "true") return;
    if (this.#variants.length === 0 || this.#options.length === 0) return;

    // Products with 2 or fewer options don't need cross-option visibility filtering
    // But still update the size displays for Sock/Insole products
    if (this.#options.length <= 2) {
      this.#updateSelectedValueDisplays();
      return;
    }
    // Prevent infinite loops when visibility update triggers a change event
    if (this.#isUpdatingVisibility) return;
    this.#isUpdatingVisibility = true;

    const colorOptionPosition = this.#getColorOptionPosition();
    const inputsByPosition = {};

    // Group all radio inputs by their option position
    const allInputs = this.querySelectorAll('input[type="radio"]');
    allInputs.forEach(input => {
      if (!(input instanceof HTMLInputElement)) return;

      const inputId = input.dataset.inputId;
      if (!inputId) return;

      const position = inputId.split("-")[0];
      if (!inputsByPosition[position]) inputsByPosition[position] = [];
      inputsByPosition[position].push(input);
    });

    const sortedPositions = Object.keys(inputsByPosition).sort((a, b) => parseInt(a) - parseInt(b));
    let currentSelections = this.#getSelectedOptions();
    let selectionChanged = false;

    // Run visibility update in multiple passes until stable
    // This ensures that hiding an option in one position can cascade to other positions
    const maxPasses = 5;
    let passCount = 0;

    while (passCount < maxPasses) {
      passCount++;
      let anyChangedThisPass = false;
      currentSelections = this.#getSelectedOptions();

      sortedPositions.forEach(position => {
        // Skip color options - they should always be visible
        if (position === colorOptionPosition?.toString()) return;

        const inputs = inputsByPosition[position];
        let hasVisibleSelected = false;
        let firstVisibleInput = null;

        inputs.forEach(input => {
          const label = input.closest("label");
          if (!label) return;

          // Build test options from ALL current selections EXCEPT this position
          // Then add the value being tested for this position
          const testOptions = { ...currentSelections };
          testOptions[position] = input.value;

          const variant = this.#findVariantForOptions(testOptions);
          const isValid = variant !== null;
          const isVariantAvailable = variant?.available === true;

          if (isValid) {
            // Show the option - a variant exists with this combination
            label.classList.remove("hidden");
            label.style.display = "";
            if (!firstVisibleInput) firstVisibleInput = input;
            if (input.checked) hasVisibleSelected = true;
          } else {
            // Hide the option - no variant exists with this combination
            label.classList.add("hidden");
            label.style.display = "none";
          }

          // Update the data-option-available attribute
          input.dataset.optionAvailable = isVariantAvailable.toString();
        });

        // If the currently selected option is now hidden, select the first visible one
        if (!hasVisibleSelected && firstVisibleInput) {
          firstVisibleInput.checked = true;
          currentSelections[position] = firstVisibleInput.value;
          selectionChanged = true;
          anyChangedThisPass = true;
        }
      });

      // If no changes this pass, we're stable
      if (!anyChangedThisPass) break;
    }

    // Update visual states after all visibility changes are processed
    this.#updateSelectedStates();
    this.#updateSelectedValueDisplays();

    // If selection changed due to hidden option, trigger a change event to update variant
    if (selectionChanged) {
      // Find a checked input from a non-color fieldset to trigger variant update
      const lastChangedInput = this.querySelector('.variant-option--buttons input[type="radio"]:checked');
      if (lastChangedInput) {
        // Use a small delay to ensure DOM is updated and reset flag after event is processed
        requestAnimationFrame(() => {
          this.#isUpdatingVisibility = false;
          lastChangedInput.dispatchEvent(new Event("change", { bubbles: true }));
        });
        return; // Exit early, flag will be reset in requestAnimationFrame
      }
    }

    this.#isUpdatingVisibility = false;
  }

  /**
   * Updates the visual selected states on option labels.
   * Used in cart-upsell context to reflect the current selection.
   */
  #updateSelectedStates() {
    const allInputs = this.querySelectorAll('input[type="radio"]');
    allInputs.forEach(input => {
      if (!(input instanceof HTMLInputElement)) return;

      const label = input.closest("label");
      if (!label) return;

      const isSelected = input.checked;

      // Handle variant option buttons (non-color swatches)
      if (label.classList.contains("variant-option__button-label")) {
        label.classList.toggle("variant-option__button-selected", isSelected);
      }

      // Handle color swatches
      const swatchSpan = label.querySelector(".productCard__swatch");
      if (swatchSpan) {
        swatchSpan.classList.toggle("swatch-selected", isSelected);
      }
    });
  }

  /**
   * Updates the selected value displays in legends.
   * Used in cart-upsell context to show the current selection.
   */
  #updateSelectedValueDisplays() {
    // Update color option display
    const colorValueDisplay = this.querySelector("[data-selected-color-value]");
    if (colorValueDisplay) {
      const checkedColorInput = this.querySelector('.variant-option--color-swatches input[type="radio"]:checked');
      if (checkedColorInput instanceof HTMLInputElement) {
        colorValueDisplay.textContent = checkedColorInput.value;
      }
    }

    // Update other option displays
    const optionValueDisplays = this.querySelectorAll("[data-selected-option-value]");

    // Size mappings for Sock and Insole products
    const sockSizeMap = {
      M: "M (US shoe size 7-9)",
      L: "L (US shoe size 9.5-11)",
      XL: "XL (US shoe size 11.5-16)",
    };

    const insoleSizeMap = {
      XS: "XS (Men's US Shoe Size 4.5-5.5)",
      S: "S (Men's US Shoe Size 6-7)",
      M: "M (Men's US Shoe Size 7.5-8.5)",
      L: "L (Men's US Shoe Size 9-10)",
      XL: "XL (Men's US Shoe Size 10.5-11.5)",
      "2XL": "2XL (Men's US Shoe Size 12-13)",
      "3XL": "3XL (Men's US Shoe Size 14-16)",
    };

    const productType = this.dataset.productType;

    optionValueDisplays.forEach(display => {
      const fieldset = display.closest("fieldset");
      if (!fieldset) return;

      const checkedInput = fieldset.querySelector('input[type="radio"]:checked');
      if (checkedInput instanceof HTMLInputElement) {
        const value = checkedInput.value;
        const optionName = checkedInput.name?.toLowerCase() || "";
        const isSizeOption = optionName.includes("size");

        if (isSizeOption && productType === "Sock") {
          display.textContent = sockSizeMap[value] || value;
        } else if (isSizeOption && productType === "Insole") {
          display.textContent = insoleSizeMap[value] || value;
        } else {
          display.textContent = value;
        }
      }
    });
  }

  /**
   * Hides color fieldsets when a picker is used for options-only layout.
   * @param {HTMLElement} root - Root element to search within.
   */
  #hideColorFieldsets(root) {
    const colorFieldsets = root.querySelectorAll(".variant-option--color-swatches");
    colorFieldsets.forEach(fieldset => {
      fieldset.classList.add("visually-hidden");
      fieldset.setAttribute("aria-hidden", "true");

      const inputs = fieldset.querySelectorAll("input");
      inputs.forEach(input => {
        input.tabIndex = -1;
      });
    });
  }

  /**
   * Finds the main cart upsell picker when a colors-only picker is used.
   * @returns {VariantPicker | null} The main picker instance or null.
   */
  #getMainCartUpsellPicker() {
    const cartUpsellItem = this.closest("article");
    if (!cartUpsellItem) return null;

    const picker = cartUpsellItem.querySelector(
      'variant-picker[data-cart-upsell="true"]:not([data-colors-only="true"])'
    );

    if (picker instanceof VariantPicker) return picker;
    return null;
  }
}

if (!customElements.get("variant-picker")) {
  customElements.define("variant-picker", VariantPicker);
}
